import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import usePrevious from 'hooks/usePrevious';

import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import ErrorOutline from '@material-ui/icons/ErrorOutline';

import Disconnected from 'components/Error/Disconnected';

function Offline(props) {
  const { connecting, displayWarning, worksOffline, error, children, onReconnect } = props;
  const connected = useSelector(state => state.device.connected);
  const previouslyConnected = usePrevious(connected);

  useEffect(() => {
    if (connected && !previouslyConnected && typeof onReconnect === 'function') {
      onReconnect();
    }
  });

  if (connecting && !worksOffline) {
    // We expect the child components to display their own loading state.
    return children;
  }

  if (connected) {
    if (error && !connecting && !worksOffline && previouslyConnected === true) {
      // We are connected, but there was an error from the API.
      // Maybe the API is down or erroring. Apologise to the user
      // TODO It would be good to log these to CloudWatch.
      return (
        <Alert
          className="margin-top-20"
          icon={<ErrorOutline color="secondary" fontSize="inherit" />}
          severity="info"
        >
          <AlertTitle>Whoops!</AlertTitle>
          We can&apos;t show this content right now. Please try again later.
        </Alert>
      );
    }

    // Connected and no error - all is fine
    return children;
  }

  // Not connected
  if (worksOffline) {
    if (displayWarning) {
      // Connection is recommended, but not essential
      return (
        <React.Fragment>
          <Alert
            className="margin-top-20"
            icon={<ErrorOutline color="secondary" fontSize="inherit" />}
            severity="info"
          >
            <AlertTitle>You are currently offline</AlertTitle>
            Connect to the Internet for full functionality
          </Alert>
          {children}
        </React.Fragment>
      );
    }
    return children;
  }

  // Connection required
  return <Disconnected />;
}

Offline.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  connecting: PropTypes.bool,
  displayWarning: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  error: PropTypes.object,
  onReconnect: PropTypes.func,
  worksOffline: PropTypes.bool,
};

Offline.defaultProps = {
  children: null,
  connecting: false,
  displayWarning: false,
  error: null,
  onReconnect: null,
  worksOffline: false,
};

export default Offline;
